import {
  DeviceTypeTotals,
  ISummary, // eslint-disable-next-line import/no-relative-packages
} from '../../../universal-dashboard-api/src/Data-Processor/Entities/Interfaces/Summary';

const OTHER_KEY = 'Other';

function mutateDeviceTypeTotals(
  deviceTypeTotals: DeviceTypeTotals,
): DeviceTypeTotals {
  const assetPowerBreakdownByPower = deviceTypeTotals.assetPowerBreakdown
    .slice()
    .sort((a, b) => b.value - a.value);

  /**
   * Month power and asset growth values are based on the total calculated for the 1st of each month.
   *
   * To avoid potential customer confusion between the total power for the customer type
   * (which refers to the value on the current date) and the most recent month growth chart values being
   * inconsistent, we modify the current month's chart value to match.
   */
  const lastMonthIndex = deviceTypeTotals.growth.length - 1;
  const currentMonthPowerGrowth = deviceTypeTotals.growth[lastMonthIndex];
  const currentMonthAssetGrowth = deviceTypeTotals.assetGrowth[lastMonthIndex];

  const growth = [
    ...deviceTypeTotals.growth.slice(0, lastMonthIndex),
    {
      ...currentMonthPowerGrowth,
      totalSize: deviceTypeTotals.totalPower,
    },
  ];

  const assetGrowth = [
    ...deviceTypeTotals.assetGrowth.slice(0, lastMonthIndex),
    {
      ...currentMonthAssetGrowth,
      totalDevices: deviceTypeTotals.totalCount,
    },
  ];

  if (assetPowerBreakdownByPower.length <= 6) {
    return {
      ...deviceTypeTotals,
      assetGrowth,
      growth,
      assetPowerBreakdown: assetPowerBreakdownByPower,
    };
  }

  const top5AssetsPower = assetPowerBreakdownByPower.slice(0, 5);
  const otherAssetPower = assetPowerBreakdownByPower.slice(5).reduce(
    (acc, curr) => ({
      ...acc,
      value: acc.value + curr.value,
    }),
    { type: OTHER_KEY, value: 0 },
  );

  const top5AssetsCount = deviceTypeTotals.assetCountBreakdown.filter(
    ({ type }) => top5AssetsPower.some((v) => v.type === type),
  );
  const otherAssetCount = deviceTypeTotals.assetCountBreakdown
    .filter(({ type }) => top5AssetsPower.every((v) => v.type !== type))
    .reduce(
      (acc, curr) => ({
        ...acc,
        value: acc.value + curr.value,
      }),
      { type: OTHER_KEY, value: 0 },
    );

  const mutatedLocations = deviceTypeTotals.locations.map((location) => ({
    ...location,
    type: top5AssetsPower.some(({ type }) => location.type === type)
      ? location.type
      : OTHER_KEY,
  }));

  return {
    ...deviceTypeTotals,
    assetCountBreakdown: [...top5AssetsCount, otherAssetCount],
    assetGrowth,
    assetPowerBreakdown: [...top5AssetsPower, otherAssetPower],
    growth,
    locations: mutatedLocations,
  };
}

function mutateDomesticDeviceTypeTotals(
  deviceTypeTotals: DeviceTypeTotals,
): DeviceTypeTotals {
  const assetPowerBreakdownByPower = deviceTypeTotals.assetPowerBreakdown
    .slice()
    .sort((a, b) => b.value - a.value);

  /**
   * Month power and asset growth values are based on the total calculated for the 1st of each month.
   *
   * To avoid potential customer confusion between the total power for the customer type
   * (which refers to the value on the current date) and the most recent month growth chart values being
   * inconsistent, we modify the current month's chart value to match.
   */

  const homeMiniAssetCount = deviceTypeTotals.assetCountBreakdown.find(
    ({ type }) => type === 'Home Mini',
  );

  const lastMonthIndex = deviceTypeTotals.growth.length - 1;
  const currentMonthPowerGrowth = deviceTypeTotals.growth[lastMonthIndex];
  const currentMonthAssetGrowth = deviceTypeTotals.assetGrowth[lastMonthIndex];

  const growth = [
    ...deviceTypeTotals.growth.slice(0, lastMonthIndex),
    {
      ...currentMonthPowerGrowth,
      totalSize: deviceTypeTotals.totalPower,
    },
  ];

  const assetGrowth = [
    ...deviceTypeTotals.assetGrowth.slice(0, lastMonthIndex),
    {
      ...currentMonthAssetGrowth,
      totalDevices:
        deviceTypeTotals.totalCount - (homeMiniAssetCount?.value ?? 0),
    },
  ];

  if (assetPowerBreakdownByPower.length <= 6) {
    return {
      ...deviceTypeTotals,
      assetGrowth,
      growth,
      assetPowerBreakdown: assetPowerBreakdownByPower,
    };
  }

  const top5AssetsPower = assetPowerBreakdownByPower.slice(0, 5);
  const otherAssetPower = assetPowerBreakdownByPower.slice(5).reduce(
    (acc, curr) => ({
      ...acc,
      value: acc.value + curr.value,
    }),
    { type: OTHER_KEY, value: 0 },
  );

  const top5AssetsCount = deviceTypeTotals.assetCountBreakdown.slice(0, 5);

  const otherAssetCount = deviceTypeTotals.assetCountBreakdown.slice(5).reduce(
    (acc, curr) => ({
      ...acc,
      value: acc.value + curr.value,
    }),
    { type: OTHER_KEY, value: 0 },
  );

  const mutatedLocations = deviceTypeTotals.locations.map((location) => ({
    ...location,
    type: top5AssetsPower.some(({ type }) => location.type === type)
      ? location.type
      : OTHER_KEY,
  }));

  return {
    ...deviceTypeTotals,
    assetCountBreakdown: [...top5AssetsCount, otherAssetCount],
    assetGrowth,
    assetPowerBreakdown: [...top5AssetsPower, otherAssetPower],
    growth,
    locations: mutatedLocations,
  };
}

/**
 * Mutates the summary data to limit to at most six asset types.
 *
 * If the number of devices exceeds 6, then will return top 5 assets
 * and fold others into the "Other" category.
 */
export function mutateSummaryData(summary: ISummary): ISummary {
  return {
    ...summary,
    commercial: mutateDeviceTypeTotals(summary.commercial),
    domestic: mutateDomesticDeviceTypeTotals(summary.domestic),
  };
}
